import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiFetch } from './api.ts';
import { useSession } from './session.tsx';

/**
 * Suche beim Server für die Auswahl mit Suche (Ticket 1.257): Kunde, Artikel und Auftrag (Story 23.1) über die globale Suche
 * (`POST /api/v1/search`, Gruppen `customers`/`articles`/`orders`). Der Server faltet, begrenzt („Treffer je Gruppe“) und meldet
 * `too_short` mit der Mindestlänge; kein Blättern – `more` zeigt „Weitere Treffer – genauer suchen.“
 */

/** Wartezeit nach dem letzten Tastendruck, bevor gefragt wird (Bedien-Konstante, keine fachliche Grenze). */
export const COMBO_SEARCH_DELAY_MS = 200;

export interface ComboSearchHit {
  id: string;
  title: string;
  subtitle: string | null;
}

interface SearchAnswer {
  status: 'ok' | 'too_short';
  minTermLength: number;
  groups: { group: string; hits: ComboSearchHit[]; more: boolean }[];
}

/** Suchtext erst nach kurzer Pause weitergeben. */
export function useDebounced(value: string, delay = COMBO_SEARCH_DELAY_MS): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export interface ComboSearchState {
  hits: ComboSearchHit[];
  /** Wartet auf die Antwort zum aktuellen Text (inkl. Pause). */
  loading: boolean;
  error: boolean;
  /** Mindestlänge laut Server, wenn der Text zu kurz ist. */
  tooShort: number | null;
  more: boolean;
}

/** Treffer einer Gruppe der globalen Suche zum Text `q` (leer: keine Anfrage). */
export function useGroupSearch(group: 'customers' | 'articles' | 'orders', q: string): ComboSearchState {
  const { getToken } = useSession();
  const debounced = useDebounced(q.trim());
  const query = useQuery({
    queryKey: ['combobox-search', group, debounced],
    enabled: debounced !== '',
    queryFn: () => apiFetch<SearchAnswer>(getToken, '/api/v1/search', { method: 'POST', body: { q: debounced } }),
  });
  const waiting = q.trim() !== debounced || (debounced !== '' && query.isFetching);
  const data = waiting ? undefined : query.data;
  const found = data?.groups.find((g) => g.group === group);
  return {
    hits: found?.hits ?? [],
    loading: q.trim() !== '' && waiting,
    error: !waiting && query.isError,
    tooShort: data?.status === 'too_short' ? data.minTermLength : null,
    more: found?.more ?? false,
  };
}
